import {Canvas, useFrame, useThree} from "@react-three/fiber";
import {observer_mgl} from "mobx-graphlink";
import React, {useMemo} from "react";
import {Row} from "react-vcomponents";
import * as THREE from "three";
import {LHQScene, sc} from "./VR/LHQ/Scene.js";
import {WaitXThenRun} from "js-vextensions";
import {store} from "../../Store/index.js";
import {hudCameras_farClipValues, hudCameras_nearClipValues} from "./VR/3DComps/LightmapOverlay.js";
import {vrLayers} from "../../Utils/ThreeJS/XR/VRLayers.js";

/** Applies the VR tonemapping setting to the renderer each frame (works fine post-creation; the
 * USD setting can change at runtime and the R3F canvas owns the renderer, so we use useFrame rather
 * than a `gl` prop). Same pattern as MapSurface's ToneMappingController. */
const ToneMappingController = observer_mgl(()=>{
	const {gl} = useThree();
	const toneMapping = store.main.settings.vr.toneMapping;
	useFrame(()=>{
		gl.toneMapping = toneMapping as THREE.ToneMapping;
		// NoToneMapping → linear output so the sRGB encode is the identity (matches the map-side convention).
		gl.outputColorSpace = toneMapping == 0 ? THREE.LinearSRGBColorSpace : THREE.SRGBColorSpace;
		gl.toneMappingExposure = 1.0;
	});
	return null;
});

export const VRUI = observer_mgl((props: {})=>{
	let {} = props;

	const canvasRef = React.useRef<HTMLCanvasElement>(null!);
	//const cameraRef = React.useRef<THREE.OrthographicCamera>(null!);

	const self = useMemo(()=>({
		canvas: (()=>null) as ()=>HTMLCanvasElement|n,
		xrCam_initialNear: 0.1,
		xrCam_initialFar: 1000,
		camera: g.LHQUI_cam = (()=>{
			const cam = new THREE.PerspectiveCamera( 70,
				//window.innerWidth / (window.innerHeight - (50 + 45)),
				window.innerWidth / window.innerHeight, // Keep this as is! IWER/VR forces canvas to window width/height, so we must use the same aspect-ratio. (this means non-vr's nav-bar occludes top/bottom edges, but that's okay)
				0.1, 1000 );
			for (const [layerName, layerNumber] of Object.entries(vrLayers)) {
				cam.layers.enable(layerNumber);
			}
			//cam.position.set( 0, 100 * sc, 200 * sc );

			// lock the "near" and "far" properties, by setting a getter for them (with no setter to change the value) [needed because otherwise the <Hud> used by LHQOverlay calls render() -> xr.updateCamera(), which]
			/*Object.defineProperty(camForThisRender, "near", {get: ()=>0.1});
			Object.defineProperty(camForThisRender, "far", {get: ()=>0.1});*/

			// close
			// note: scene ROOT is y-up (the ZUpGroup in Scene.tsx handles the z-up content), so stock y-up pose is correct
			cam.position.set(3, 2, 4);
			cam.setRotationFromEuler(new THREE.Euler(0, 1.1, 0));

			// far, back of room
			//cam.position.set(4, 5, 1.3);
			//cam.setRotationFromEuler(new THREE.Euler(-2.7, 1, 1.5));
			//cam.rotation.copy(new THREE.Euler(-1.5, 1, 1.5));
			//cam.rotation.copy(new THREE.Euler(-1.26, 0.92, 1.2));
			/*cam.rotation.set(-1.26, 0.92, 1.2);
			cam.updateProjectionMatrix();*/
			/*cam.lookAt(-10, 0, 0);
			WaitXThenRun(1000, ()=>cam.lookAt(-10, 0, 0));*/

			//cam.position.set( 0, 3, 7 );
			cam.name = "Camera";
			return cam;
		})(),
	}), []);
	Object.assign(self, {canvas: ()=>canvasRef.current /*, camera: ()=>cameraRef.current*/});

	return (
		<Row id="threejs-container"
			tabIndex={-1} // so event-handlers on this element can receive keyboard-events (see MapControls.ts)
			style={{
				position: "fixed", left: 0, top: 0,
				width: "100%", height: "100%", // Keep this as is! IWER/VR forces canvas to window width/height, so we must use the same aspect-ratio. (this means non-vr's nav-bar occludes top/bottom edges, but that's okay)
				touchAction: "none",
			}}
		>
			<style>{`
				.sub1 { opacity: 0.1 }
				.NavBar_Android { box-shadow: none !important; }
			`}</style> {/* temp */}
      	{/*<button onClick={()=>store.enterVR()}>Enter VR</button>*/}
			<Canvas /*shadows*/ ref={canvasRef}
				camera={self.camera}
				//gl={{autoClear: true, canvas: {}, autoClearColor: "black", alpha: false}}
				/*scene={{
					background: new THREE.Color("#000000"),
				}}*/
				//style={{position: "fixed", left: 0, top: 0, width: "100%", height: "100%", zIndex: 3}}
				onCreated={state=>{
					const renderer = state.gl;
					state.gl.setClearColor(new THREE.Color("#000000"), 1);
					//const mainCam_initial = state.camera;

					//state.gl.xr.cameraAutoUpdate = false;
					/*state.camera["isMainCamera"] = true;
					state.camera["far_minValToBeValid"] = 100; // if new far-value is <100*/
					if (renderer.xr && renderer.xr["updateCamera_orig"] == null) {
						renderer.xr["updateCamera_orig"] = renderer.xr.updateCamera;
						renderer.xr.updateCamera = (camForThisRender: THREE.Camera)=>{
							/*if (camForThisRender["isMainCamera"] && Object.getOwnPropertyDescriptor(camForThisRender, "far")?.get == null) {
								Object.defineProperty(camForThisRender, "far", {get() { return this["far_val"]; }, set(val) { if (val >= camForThisRender["far_minValToBeValid"]) this["far_val"] = val; }});
							}*/

							// prior to the updateCamera call executing, ensure that we "protect" non-hud cameras from having their near/far values changed to those of one of the hud cameras (otherwwise happens due to updating logic for XR cameras)
							if (camForThisRender instanceof THREE.ArrayCamera || camForThisRender instanceof THREE.PerspectiveCamera) {
								if (Object.getOwnPropertyDescriptor(camForThisRender, "near")?.get == null) {
									camForThisRender["near_val"] = camForThisRender instanceof THREE.ArrayCamera ? self.xrCam_initialNear : camForThisRender.near;
									Object.defineProperty(camForThisRender, "near", {
										get() { return this["near_val"]; },
										set(val) {
											if (hudCameras_nearClipValues.includes(val)) return;
											this["near_val"] = val;
										},
									});
								}
								if (Object.getOwnPropertyDescriptor(camForThisRender, "far")?.get == null) {
									camForThisRender["far_val"] = camForThisRender instanceof THREE.ArrayCamera ? self.xrCam_initialFar : camForThisRender.far;
									Object.defineProperty(camForThisRender, "far", {
										get() { return this["far_val"];},
										set(val) {
											if (hudCameras_farClipValues.includes(val)) return;
											this["far_val"] = val;
										},
									});
								}
							}

							return renderer.xr["updateCamera_orig"](camForThisRender);
						};
					}
				}}
			>
				<ToneMappingController/>
				<LHQScene/>
				{/*<ErrorBoundary2 set={()=>{}}>
					<React.Suspense fallback={<></>}>
						<LHQScene/>
					</React.Suspense>
				</ErrorBoundary2>*/}
			</Canvas>
		</Row>
	);
});

// Tried to add this, so that it catches the errors instead of the ErrorBoundary in CanvasImpl: https://github.com/pmndrs/react-three-fiber/blob/a6acdbfe9912718e897a4f69d993f45d8a057eec/packages/fiber/src/native/Canvas.tsx#L30
// However, while this does catch the error, it seems to still result in a broken ui/vr state (where headset rendering gets frozen/transparent) -- perhaps because error gets caught by both boundaries?
// (or maybe render errors anywhere under a react-three-fiber tree break the whole tree, regardless of error-boundaries -- perhaps due to quirk of how react-three-fiber works...)
/*class ErrorBoundary2 extends React.Component<{set: React.Dispatch<Error | undefined>; children: React.ReactNode}, {error: boolean}> {
	state = {error: false};
	static getDerivedStateFromError = ()=>({ error: true })
	componentDidCatch(err: Error) {
		console.error("ErrorBoundary2 caught error:", err);
		this.props.set(err);
	}
	render() {
		return this.state.error ? null : this.props.children;
	}
}*/